/*
 * Copyright (C) 2025 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import {
  Evaluator,
  EvaluatorForStudent,
  JobDetails,
  Mentee,
  MenteeAssignment,
  Mentor,
  MentorshipImportReport,
  MentorshipStatus,
  Student,
  StudentAssignment,
  StudentForEvaluator,
} from '@app/app/page-modules/admin/components/mentors/admin-mentors.model';
import {
  AcknowledgeFormRequest,
  CreateJobRequest,
  MenteePlaylistSummary,
  MentorFeedback,
  MentorFeedbackEventNote,
  MentorMenteeDataService,
  PendingMenteesDecisionRequest,
  RelatedUserSearchItem,
} from '@app/app/page-modules/admin/services/mentor-mentee/mentor-mentee-data.service';
import { CommentRequest } from '@app/app/page-modules/reviews/models';
import { MembersBooleanSearchRequest } from '@app/app/shared/models/admin/boolean-filters.model';
import { UserSearch } from '@app/app/shared/models/admin/members.model';
import { Page } from '@app/app/shared/models/page';
import { RestClientService } from '@app/app/shared/services/rest-client.service';
import { ObservableResult } from '@app/app/shared/store';
import { environment } from '@app/environments/environment';
import { TranslocoService } from '@ngneat/transloco';
import { catchError, switchMap } from 'rxjs/operators';

@Injectable()
export class ApiMentorMenteeDataService implements MentorMenteeDataService {
  constructor(
    private restClient: RestClientService,
    private translocoService: TranslocoService,
  ) {}
  searchByMentor(page: number, size: number, request?: MembersBooleanSearchRequest): ObservableResult<Page<Mentor>> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `mentorships/admin/search-mentors?page=${page}&size=${size}`);

    return this.restClient.post<Page<Mentor>>(url, request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorSearchMentors'))),
    );
  }

  searchByMentee(page: number, size: number, request?: MembersBooleanSearchRequest): ObservableResult<Page<Mentee>> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `mentorships/admin/search-mentees?page=${page}&size=${size}`);

    return this.restClient.post<Page<Mentee>>(url, request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorSearchMentees'))),
    );
  }

  searchByEvaluator(page: number, size: number, request?: MembersBooleanSearchRequest): ObservableResult<Page<Evaluator>> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `mentorships/admin/search-evaluators?page=${page}&size=${size}`);

    return this.restClient.post<Page<Evaluator>>(url, request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError('Error searching evaluators')),
    );
  }

  searchByStudent(page: number, size: number, request?: MembersBooleanSearchRequest): ObservableResult<Page<Student>> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `mentorships/admin/search-students?page=${page}&size=${size}`);

    return this.restClient.post<Page<Student>>(url, request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError('Error searching students')),
    );
  }

  findStudentsForEvaluator(
    page: number,
    size: number,
    evaluatorUserUid: string,
    sortBy?: string,
    sortDirection?: string,
    term?: string,
  ): ObservableResult<Page<StudentForEvaluator>> {
    const params = {};
    if (sortBy) {
      params['sortBy'] = sortBy;
    }
    if (sortDirection) {
      params['sortDirection'] = sortDirection;
    }
    if (term) {
      params['term'] = term;
    }
    const url = Location.joinWithSlash(
      environment.apiRootUrl || '',
      `mentorships/evaluators/${evaluatorUserUid}/students?page=${page}&size=${size}`,
    );

    return this.restClient.get<Page<StudentForEvaluator>>(url, params).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError('Error retrieving students')),
    );
  }

  findEvaluatorsForStudent(
    page: number,
    size: number,
    studentUserUid: string,
    sortBy?: string,
    sortDirection?: string,
    term?: string,
  ): ObservableResult<Page<EvaluatorForStudent>> {
    const params = {};
    if (sortBy) {
      params['sortBy'] = sortBy;
    }
    if (sortDirection) {
      params['sortDirection'] = sortDirection;
    }
    if (term) {
      params['term'] = term;
    }
    const url = Location.joinWithSlash(
      environment.apiRootUrl || '',
      `mentorships/students/${studentUserUid}/evaluators?page=${page}&size=${size}`,
    );

    return this.restClient.get<Page<EvaluatorForStudent>>(url, params).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError('Error retrieving evaluators')),
    );
  }

  findMenteesForMentor(
    page: number,
    size: number,
    mentorUserUid: string,
    sortBy?: string,
    sortDirection?: string,
    term?: string,
  ): ObservableResult<Page<Mentee>> {
    const params = {};
    if (sortBy) {
      params['sortBy'] = sortBy;
    }
    if (sortDirection) {
      params['sortDirection'] = sortDirection;
    }
    if (term) {
      params['term'] = term;
    }
    const url = Location.joinWithSlash(
      environment.apiRootUrl || '',
      `mentorships/mentors/${mentorUserUid}/mentees?page=${page}&size=${size}`,
    );

    return this.restClient.get<Page<Mentee>>(url, params).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorRetrieveMentees'))),
    );
  }

  findMentorsForMentee(
    page: number,
    size: number,
    menteeUserUid: string,
    sortBy?: string,
    sortDirection?: string,
    term?: string,
  ): ObservableResult<Page<Mentor>> {
    const params = {};
    if (sortBy) {
      params['sortBy'] = sortBy;
    }
    if (sortDirection) {
      params['sortDirection'] = sortDirection;
    }
    if (term) {
      params['term'] = term;
    }
    const url = Location.joinWithSlash(
      environment.apiRootUrl || '',
      `mentorships/mentees/${menteeUserUid}/mentors?page=${page}&size=${size}`,
    );

    return this.restClient.get<Page<Mentor>>(url, params).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorRetrieveMentors'))),
    );
  }

  findMentors(page: number, size: number, term?: string, statuses?: MentorshipStatus[]): ObservableResult<Page<Mentor>> {
    const params = {};
    if (term) {
      params['term'] = term;
    }
    if (statuses && statuses.length > 0) {
      params['statuses'] = statuses;
    }

    const url = Location.joinWithSlash(environment.apiRootUrl || '', `mentorships/mentors?page=${page}&size=${size}`);

    return this.restClient.get<Page<Mentor>>(url, params).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorRetrieveMentors'))),
    );
  }

  assignMenteeToMentor(mentorUserUid: string, menteeUserUid: string, status: MentorshipStatus): ObservableResult<MenteeAssignment> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `mentorships/mentors/${mentorUserUid}/mentees/${menteeUserUid}`);
    const requestBody = { status };

    return this.restClient.put<MenteeAssignment>(url, requestBody).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorAssignMentee'))),
    );
  }

  deleteMenteeFromMentor(mentorUserUid: string, menteeUserUid: string): ObservableResult<void> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `mentorships/mentors/${mentorUserUid}/mentees/${menteeUserUid}`);

    return this.restClient.delete<void>(url).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorRemoveMentee'))),
    );
  }

  assignStudentToEvaluator(
    evaluatorUserUid: string,
    studentUserUid: string,
    status: MentorshipStatus,
  ): ObservableResult<StudentAssignment> {
    const url = Location.joinWithSlash(
      environment.apiRootUrl || '',
      `mentorships/evaluators/${evaluatorUserUid}/students/${studentUserUid}`,
    );
    const requestBody = { status };

    return this.restClient.put<StudentAssignment>(url, requestBody).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorAssignStudent'))),
    );
  }

  deleteStudentFromEvaluator(evaluatorUserUid: string, studentUserUid: string): ObservableResult<void> {
    const url = Location.joinWithSlash(
      environment.apiRootUrl || '',
      `mentorships/evaluators/${evaluatorUserUid}/students/${studentUserUid}`,
    );

    return this.restClient.delete<void>(url).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorRemoveStudent'))),
    );
  }

  importMentees(menteesFile: File, sendNotifications: boolean): ObservableResult<MentorshipImportReport> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', 'mentorships/admin/import-mentees');

    const formData = new FormData();
    formData.append('file', menteesFile, menteesFile.name);
    formData.append('sendNotifications', String(sendNotifications));

    return this.restClient.post<MentorshipImportReport>(url, formData).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorImportMentee'))),
    );
  }

  postPendingMenteesDecision(decision: PendingMenteesDecisionRequest): ObservableResult<void> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', 'mentorships/pending');

    return this.restClient.post<void>(url, decision).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdatePendingMenteesStatus'))),
    );
  }

  findPendingMentees(): ObservableResult<UserSearch[]> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `mentorships/pending`);

    return this.restClient.get<UserSearch[]>(url).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorRetrieveMentees'))),
    );
  }

  getAllFeedbackForLearnerPlaylist(playlistUid: string, learnerUserUid: string): ObservableResult<MentorFeedback[]> {
    const url = Location.joinWithSlash(
      environment.apiRootUrl || '',
      `mentorships/feedback/playlists/${playlistUid}/users/${learnerUserUid}`,
    );

    return this.restClient.get<MentorFeedback[]>(url).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorRetrieveFeedback'))),
    );
  }

  getSingleMentorFeedbackForLearnerPlaylist(
    playlistUid: string,
    learnerUserUid: string,
    mentorUserUid: string,
  ): ObservableResult<MentorFeedback> {
    const url = Location.joinWithSlash(
      environment.apiRootUrl || '',
      `mentorships/feedback/playlists/${playlistUid}/users/${learnerUserUid}/mentors/${mentorUserUid}`,
    );

    return this.restClient.get<MentorFeedback>(url).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorRetrieveFeedback'))),
    );
  }

  createNoteOnForm(
    playlistUid: string,
    cardUid: string,
    formUid: string,
    learnerUserUid: string,
    request: CommentRequest,
  ): ObservableResult<MentorFeedbackEventNote> {
    const url = Location.joinWithSlash(
      environment.apiRootUrl || '',
      `mentorships/feedback/playlists/${playlistUid}/cards/${cardUid}/forms/${formUid}/users/${learnerUserUid}/notes`,
    );

    return this.restClient.post<MentorFeedbackEventNote>(url, request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorCreateNote'))),
    );
  }

  updateNoteOnForm(
    playlistUid: string,
    cardUid: string,
    formUid: string,
    learnerUserUid: string,
    noteUid: string,
    request: CommentRequest,
  ): ObservableResult<MentorFeedbackEventNote> {
    const url = Location.joinWithSlash(
      environment.apiRootUrl || '',
      `mentorships/feedback/playlists/${playlistUid}/cards/${cardUid}/forms/${formUid}/users/${learnerUserUid}/notes/${noteUid}`,
    );

    return this.restClient.patch<MentorFeedbackEventNote>(url, request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateNote'))),
    );
  }

  deleteNoteOnForm(playlistUid: string, cardUid: string, formUid: string, learnerUserUid: string, noteUid: string): ObservableResult<void> {
    const url = Location.joinWithSlash(
      environment.apiRootUrl || '',
      `mentorships/feedback/playlists/${playlistUid}/cards/${cardUid}/forms/${formUid}/users/${learnerUserUid}/notes/${noteUid}`,
    );

    return this.restClient.delete<void>(url).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorDeleteNote'))),
    );
  }

  getSubmissionsFromMentee(
    mentorUserUid: string,
    menteeUserUid: string,
    page: number,
    size: number,
  ): ObservableResult<Page<MenteePlaylistSummary>> {
    const url = Location.joinWithSlash(
      environment.apiRootUrl || '',
      `mentorships/mentors/${mentorUserUid}/mentees/${menteeUserUid}/submissions?page=${page}&size=${size}`,
    );

    return this.restClient.get<Page<MenteePlaylistSummary>>(url).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError('translations.errors.errorRetrieveMenteePlaylists')),
    );
  }

  getMentee(menteeUserUid: string): ObservableResult<Mentee> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `mentorships/mentees/${menteeUserUid}`);

    return this.restClient.get<Mentee>(url).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorRetrieveMentees'))),
    );
  }

  getRelatedUser(relatedUserUid: string): ObservableResult<RelatedUserSearchItem> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', `mentorships/users/${relatedUserUid}`);

    return this.restClient.get<RelatedUserSearchItem>(url).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError('Error retrieving related user')),
    );
  }

  patchFormAcknowledgement(
    playlistUid: string,
    cardUid: string,
    formUid: string,
    menteeUserUid: string,
    isAcknowledged: boolean,
  ): ObservableResult<void> {
    const url = Location.joinWithSlash(
      environment.apiRootUrl || '',
      `mentorships/feedback/playlists/${playlistUid}/cards/${cardUid}/forms/${formUid}/users/${menteeUserUid}/acknowledgement`,
    );

    const request: AcknowledgeFormRequest = {
      acknowledged: isAcknowledged,
    };

    return this.restClient.patch<void>(url, request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorPatchFormAcknowledgement'))),
    );
  }

  getAllJobs(): ObservableResult<JobDetails[]> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', 'mentorships/jobs');

    return this.restClient.get<JobDetails[]>(url).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError('Error retrieving jobs')),
    );
  }

  createJob(request: CreateJobRequest): ObservableResult<JobDetails> {
    const url = Location.joinWithSlash(environment.apiRootUrl || '', 'mentorships/jobs');

    return this.restClient.post<JobDetails>(url, request).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError('Error creating job')),
    );
  }

  assignJobToStudentEvaluator(evaluatorUserUid: string, studentUserUid: string, jobId: string): ObservableResult<void> {
    const url = Location.joinWithSlash(
      environment.apiRootUrl || '',
      `mentorships/evaluators/${evaluatorUserUid}/students/${studentUserUid}/jobs/${jobId}`,
    );

    return this.restClient.put<void>(url, {}).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError('Error assigning job')),
    );
  }

  removeJobFromStudentEvaluator(evaluatorUserUid: string, studentUserUid: string, jobId: string): ObservableResult<void> {
    const url = Location.joinWithSlash(
      environment.apiRootUrl || '',
      `mentorships/evaluators/${evaluatorUserUid}/students/${studentUserUid}/jobs/${jobId}`,
    );

    return this.restClient.delete<void>(url).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError('Error removing job')),
    );
  }
}
